import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/router";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper/modules";
import { FiHome, FiUser, FiMapPin, FiExternalLink } from "react-icons/fi";

// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

export default function ShortletsCarousel() {
  const [shortlets, setShortlets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const router = useRouter();

  useEffect(() => {
    fetchShortlets();
  }, []);

  const fetchShortlets = async () => {
    try {
      setLoading(true);
      const response = await fetch("/api/shortlets/featured");
      const data = await response.json();

      if (data.success) {
        setShortlets(data.shortlets);
      } else {
        setError(data.error || "Failed to fetch shortlets");
      }
    } catch (err) {
      console.error("Error fetching shortlets:", err);
      setError("Failed to load shortlets");
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (!price) return "Price on request";
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const handleViewListing = (listingId) => {
    const url = `https://shortlets.topdial.ng/listings/${listingId}`;
    
    // Use router.push to navigate to external URL
    // This should work better in PWA context
    router.push(url);
  };

  const getImageSrc = (images) => {
    if (!images || images.length === 0) {
      return "/landhm.jpg"; // Use existing image as placeholder
    }
    
    // Handle different image structure formats
    if (Array.isArray(images)) {
      // If it's an array of objects with url property
      if (images[0] && typeof images[0] === 'object' && images[0].url) {
        return images[0].url;
      }
      // If it's an array of strings
      if (typeof images[0] === 'string') {
        return images[0];
      }
    }
    
    // If images is a string
    if (typeof images === 'string') {
      return images;
    }
    
    return "/landhm.jpg"; // Fallback
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-wine"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-gray-500 p-8 bg-gray-50 rounded-lg">
        <p className="mb-2">Unable to load shortlets</p>
        <p className="text-sm">{error}</p>
      </div>
    );
  }

  if (!shortlets || shortlets.length === 0) {
    return (
      <div className="text-center text-gray-500 p-8 bg-gray-50 rounded-lg">
        <p>No shortlets available at the moment</p>
      </div>
    );
  }

  return (
    <div className="shortlets-carousel">
      <Swiper
        modules={[Pagination, Autoplay]}
        spaceBetween={24}
        slidesPerView={1}
        pagination={{
          clickable: true,
          dynamicBullets: true,
        }}
        autoplay={{
          delay: 90000, // 90 seconds
          disableOnInteraction: false,
        }}
        loop={shortlets.length > 3}
        breakpoints={{
          // Mobile: 1 slide
          320: {
            slidesPerView: 1,
            spaceBetween: 16,
          },
          // Tablet: 2 slides
          768: {
            slidesPerView: 2,
            spaceBetween: 20,
          },
          // Desktop: 3 slides
          1024: {
            slidesPerView: 3,
            spaceBetween: 24,
          },
        }}
        className="mySwiper"
      >
        {shortlets.map((shortlet) => (
          <SwiperSlide key={shortlet._id}>
            <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
              {/* Image - Matching featured listings height */}
              <div className="relative h-48 overflow-hidden">
                <Image
                  src={getImageSrc(shortlet.images)}
                  alt={shortlet.title || "Shortlet"}
                  fill
                  sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                  className="object-cover hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute top-2 right-2 bg-wine text-white px-2 py-1 rounded-md text-xs font-medium">
                  Shortlet
                </div>
              </div>

              {/* Content - More compact */}
              <div className="p-3">
                <h3 className="font-semibold text-base text-gray-900 mb-1 line-clamp-1">
                  {shortlet.title || "Beautiful Shortlet"}
                </h3>

                {/* Location */}
                {shortlet.location && (
                  <div className="flex items-center text-gray-600 mb-2">
                    <FiMapPin className="w-3 h-3 mr-1" />
                    <span className="text-xs truncate">
                      {typeof shortlet.location === 'string' 
                        ? shortlet.location 
                        : shortlet.location.address || shortlet.location.city || "Location not specified"}
                    </span>
                  </div>
                )}

                {/* Features */}
                <div className="flex items-center gap-3 text-gray-600 mb-2">
                  {shortlet.bedrooms && (
                    <div className="flex items-center">
                      <FiHome className="w-3 h-3 mr-1" />
                      <span className="text-xs">{shortlet.bedrooms} bed</span>
                    </div>
                  )}
                  {shortlet.bathrooms && (
                    <div className="flex items-center">
                      <FiUser className="w-3 h-3 mr-1" />
                      <span className="text-xs">{shortlet.bathrooms} bath</span>
                    </div>
                  )}
                </div>

                {/* Price */}
                <div className="mb-2">
                  <span className="text-lg font-bold text-wine">
                    {formatPrice(shortlet.price)}
                  </span>
                  <span className="text-gray-500 text-xs ml-1">/night</span>
                </div>

                {/* Description - More compact */}
                {shortlet.description && (
                  <p className="text-gray-600 text-xs mb-2 line-clamp-1">
                    {shortlet.description}
                  </p>
                )}

                {/* View Button - Smaller */}
                <button
                  onClick={() => handleViewListing(shortlet._id)}
                  className="w-full bg-wine text-white py-1.5 px-3 rounded-md hover:bg-wine/90 transition-colors duration-200 flex items-center justify-center text-sm"
                >
                  <span>View Details</span>
                  <FiExternalLink className="w-3 h-3 ml-1" />
                </button>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      <style jsx global>{`
        .mySwiper .swiper-pagination-bullet {
          background: #722f37;
          opacity: 0.3;
        }
        .mySwiper .swiper-pagination-bullet-active {
          opacity: 1;
        }
        .line-clamp-1 {
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}
